import { NavLink, useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import { List, useTheme, Typography } from '@mui/material'
import FsMenuItem from '../fs-menu-item/fs-menu-item'
import { getIcon } from '../common/Icons'
import FsRoutes from '../../FsRoutes'
import { StaticView } from '../../shared/model'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { WALKTHROUGH_STATUS } from '../../shared/constants'
import { changeWalkthroughStatus } from '../../store/actions'

type NavigationParams = {
  id?: string
}

const FsNavigation = ({
  isHovered,
  onClose,
}: {
  isHovered?: boolean
  onClose: () => void
}) => {
  const { id } = useParams<NavigationParams>()
  const location = useLocation()
  const contextModelVariantId = parseInt(id!, 10)
  const { modules, runs } = useAppSelector((state) => state.currentAnalysis)
  const { walkthroughStatus } = useAppSelector((state) => state.currentUser)
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { t } = useTranslation()

  const findCurrentModule = () =>
    modules?.find((module) =>
      module.contextModels.some((cm) =>
        cm.contextModelVariants.some((cmv) => cmv.id === contextModelVariantId),
      ),
    )

  const routeForModule = (moduleName: string) => FsRoutes.find((route) => route.name === moduleName)

  const hasRouteForModule = (moduleName: string) =>
    FsRoutes.find((route) => route.name === moduleName) !== undefined

  const dashboard = routeForModule(StaticView.DASHBOARD)
  const reports = routeForModule(StaticView.REPORTS)
  const etl = routeForModule(StaticView.ETL)

  const noAnalysisYet = runs && runs.length === 0

  return (
    <List sx={{ overflowY: 'auto', overflowX: 'hidden' }}>
      {noAnalysisYet ? (
        <NavLink
          to={etl!.path}
          style={{ textDecoration: 'none', justifyContent: 'flex-start' }}
          key={`nav-link-${etl!.name}`}
        >
          <ListItem
            key={etl!.name}
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'center',
              backgroundColor: location.pathname === '/fs/etl' ? theme.palette.action.hover : '',
              py: 1.25,
              px: 1.25,
              borderRadius: 1,
              mt: 1,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 26,
              }}
            >
              {getIcon(etl!.name, {
                color: theme.palette.primary.main,
                width: 26,
                height: 26,
              })}
            </ListItemIcon>
            {isHovered && (
              <Typography
                ml={1}
                color={
                  location.pathname === '/fs/etl'
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary
                }
              >
                {t(etl!.name as any)}
              </Typography>
            )}
          </ListItem>
        </NavLink>
      ) : (
        <>
          <NavLink
            to={dashboard!.path}
            style={{ textDecoration: 'none', justifyContent: 'flex-start' }}
            key={`nav-link-${dashboard!.name}`}
          >
            <ListItem
              key={dashboard!.name}
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'center',
                backgroundColor:
                  location.pathname === '/fs/dashboard' ? theme.palette.action.hover : '',
                py: 1.25,
                px: 1.5,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 24,
                }}
              >
                {getIcon(dashboard!.name, {
                  color: theme.palette.primary.main,
                  width: 20,
                  height: 24,
                })}
              </ListItemIcon>
              {isHovered && (
                <Typography
                  ml={1}
                  color={
                    location.pathname === '/fs/dashboard'
                      ? theme.palette.text.primary
                      : theme.palette.text.secondary
                  }
                >
                  {t(dashboard!.name as any)}
                </Typography>
              )}
            </ListItem>
          </NavLink>
          <div className=".menu">
            {modules
              ?.filter((it) => hasRouteForModule(it.name))
              .map((module) => (
                <FsMenuItem
                  key={`fs-menu-item-${module.name}`}
                  module={module}
                  currentModule={findCurrentModule()}
                  isHovered={isHovered}
                  onClose={onClose}
                />
              ))}
          </div>

          <NavLink
            to={reports!.path}
            style={{ textDecoration: 'none', justifyContent: 'flex-start' }}
            key={`nav-link-${reports!.name}`}
            onClick={() => {
              if (walkthroughStatus === WALKTHROUGH_STATUS.InProgress)
                dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Paused))
            }}
          >
            <ListItem
              key={reports!.name}
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'center',
                backgroundColor:
                  location.pathname === '/fs/reports' ? theme.palette.action.hover : '',
                py: 1.25,
                px: 1.25,
                mt: 1,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 24,
                }}
              >
                {getIcon(reports!.name, {
                  color: theme.palette.primary.main,
                  width: 24,
                  height: 24,
                })}
              </ListItemIcon>
              {isHovered && (
                <Typography
                  ml={1}
                  color={
                    location.pathname === '/fs/reports'
                      ? theme.palette.text.primary
                      : theme.palette.text.secondary
                  }
                >
                  {t(reports!.name as any)}
                </Typography>
              )}
            </ListItem>
          </NavLink>

          <NavLink
            to={etl!.path}
            style={{ textDecoration: 'none', justifyContent: 'flex-start' }}
            key={`nav-link-${etl!.name}`}
            className="etlicon"
          >
            <ListItem
              key={reports!.name}
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'center',
                backgroundColor: location.pathname === '/fs/etl' ? theme.palette.action.hover : '',
                py: 1.25,
                px: 1.25,
                borderRadius: 1,
                mt: 1,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 24,
                }}
              >
                {getIcon(etl!.name, {
                  color: theme.palette.primary.main,
                  width: 26,
                  height: 26,
                })}
              </ListItemIcon>
              {isHovered && (
                <Typography
                  ml={1}
                  color={
                    location.pathname === '/fs/etl'
                      ? theme.palette.text.primary
                      : theme.palette.text.secondary
                  }
                >
                  {t(etl!.name as any)}
                </Typography>
              )}
            </ListItem>
          </NavLink>
        </>
      )}
    </List>
  )
}

export default FsNavigation
